import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import ArialNormal from "../../../../utils/Customfonts/ARIAL.TTF";
import ArialBold from "../../../../utils/Customfonts/ARIALBD.TTF";
import ArialItalic from "../../../../utils/Customfonts/ARIALI.TTF";
import ArialBoldItalic from "../../../../utils/Customfonts/ARIALBI.TTF";

const DYC_LOGO = "/img/dycgpl-logo.webp";

export const customFontFamily = {
  bold: "bold",
  normal: "normal",
  boldFont: "Arial",
  normalFont: "Arial",
  italic: "italic",
  italicFont: "Arial",
};

// -----------------------------------------------------------------------------
// GLOBAL CONFIG & STYLES (Single Page Perfect Fit)
// -----------------------------------------------------------------------------

export const config = {
  borderMargin: 16,
  contentMargin: 26,

  get contentWidth() {
    return 595.28 - this.contentMargin * 2;
  },

  fontSize: 7.8,
  fontSizeSm: 6.8,
  fontSizeTitle: 12.5,
  headerHeight: 46,
};

const getBaseStyles = () => ({
  fontSize: config.fontSize,
  cellPadding: { top: 2.5, bottom: 2.5, left: 3.5, right: 3.5 },
  lineColor: [60, 60, 60],
  lineWidth: 0.5,
  textColor: [0, 0, 0],
  fillColor: false,
  font: customFontFamily.normalFont,
  fontStyle: customFontFamily.normal,
  halign: "left",
  valign: "middle",
});

// -----------------------------------------------------------------------------
// FONT LOADING
// -----------------------------------------------------------------------------

const arrayBufferToBase64 = (buffer) => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
};

const loadAndAddFont = async (pdfDoc, fontUrl, fontNameTTF, fontName, fontStyle) => {
  try {
    const response = await fetch(fontUrl);
    const buffer = await response.arrayBuffer();
    const base64 = arrayBufferToBase64(buffer);

    pdfDoc.addFileToVFS(`${fontNameTTF}.ttf`, base64);
    pdfDoc.addFont(`${fontNameTTF}.ttf`, fontName, fontStyle);
  } catch (error) {
    console.error(`Font loading failed: ${fontName}`, error);
  }
};

// -----------------------------------------------------------------------------
// CHECKMARK & CHECKBOX DRAWING HELPER
// -----------------------------------------------------------------------------

const drawCheckmark = (doc, x, y, size = 10) => {
  doc.saveGraphicsState && doc.saveGraphicsState();
  doc.setDrawColor(0, 50, 180);
  doc.setLineWidth(1.4);
  doc.line(x + 1.5, y + size * 0.55, x + size * 0.4, y + size * 0.85);
  doc.line(x + size * 0.4, y + size * 0.85, x + size * 0.9, y + size * 0.15);
  doc.restoreGraphicsState && doc.restoreGraphicsState();
};

const drawCheckboxWithTick = (doc, x, y, checked = false, size = 10) => {
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.7);
  doc.rect(x, y, size, size);

  if (checked) {
    drawCheckmark(doc, x, y, size);
  }
};

// -----------------------------------------------------------------------------
// PAGE DECORATORS (Outer Border, Header Line, Logo, Footers)
// -----------------------------------------------------------------------------

const addPageDecorators = (doc, data = {}) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // 1. OUTER BORDER
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.8);
  doc.rect(
    config.borderMargin,
    config.borderMargin,
    pageWidth - config.borderMargin * 2,
    pageHeight - config.borderMargin * 2
  );

  // 2. FORM HEADER TITLE
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(13);
  doc.setTextColor(20, 20, 20);
  doc.text("VISUAL ACUITY EXAMINATION RECORD", pageWidth / 2, config.borderMargin + 24, {
    align: "center",
  });

  // 3. LOGO (Right side of header)
  try {
    const logoSize = 34;
    const logoX = pageWidth - config.borderMargin - logoSize - 12;
    const logoY = config.borderMargin + 6;

    try {
      doc.addImage(DYC_LOGO, "WEBP", logoX, logoY, logoSize, logoSize);
    } catch (_) {
      try {
        doc.addImage(DYC_LOGO, "PNG", logoX, logoY, logoSize, logoSize);
      } catch (__) {
        doc.addImage(DYC_LOGO, logoX, logoY, logoSize, logoSize);
      }
    }
  } catch (_) {
    // Logo optional
  }

  // 4. HEADER DIVIDER LINE
  doc.setLineWidth(0.8);
  doc.line(
    config.borderMargin,
    config.borderMargin + 44,
    pageWidth - config.borderMargin,
    config.borderMargin + 44
  );

  // 5. FOOTER
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.setFontSize(7.5);
  doc.setTextColor(40, 40, 40);

  const docCode = `${data.docCode || "DYC/FMT-MR/24"} Rev.: ${data.revNo || "00"}, Eff.Dated-${data.effDate || "24/05/2023"}`;
  const footerY1 = pageHeight - config.borderMargin - 15;
  const footerY2 = pageHeight - config.borderMargin - 5;

  // Line 1 Left
  doc.text(docCode, config.borderMargin + 8, footerY1);

  // Line 2 Left
  doc.text(
    "Company General Use. All Rights Reserved by DYC Global Pvt Ltd.",
    config.borderMargin + 8,
    footerY2
  );

  // Right Page Number
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text("Page 1 of 1", pageWidth - config.borderMargin - 50, footerY1);
};

// -----------------------------------------------------------------------------
// MAIN DRAWING FUNCTIONS
// -----------------------------------------------------------------------------

export const generateAcuityRecordPDF = async (data = {}) => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "a4",
    compress: true,
  });

  await Promise.all([
    loadAndAddFont(doc, ArialNormal, "Arial-Normal", customFontFamily.normalFont, customFontFamily.normal),
    loadAndAddFont(doc, ArialBold, "Arial-Bold", customFontFamily.boldFont, customFontFamily.bold),
    loadAndAddFont(doc, ArialItalic, "Arial-Italic", customFontFamily.italicFont, customFontFamily.italic),
    loadAndAddFont(doc, ArialBoldItalic, "Arial-BoldItalic", customFontFamily.normalFont, "bolditalic"),
  ]);

  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);

  const contentW = config.contentWidth;
  const leftX = config.contentMargin;

  // 1. Notice Text: "The following sections are to be completed by the eye examiner"
  let currentY = config.borderMargin + 58;
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(9.5);
  doc.setTextColor(0, 0, 0);
  doc.text("The following sections are to be completed by the eye examiner", leftX, currentY);
  currentY += 10;

  // ---------------------------------------------------------------------------
  // 2. CANDIDATE INFO BOX (Top Box)
  // ---------------------------------------------------------------------------
  const candidateRows = [
    [
      {
        content: `Name of individual tested (First name(s), Surname): ${data.candidateName || ""}`,
        colSpan: 3,
        styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold },
      },
    ],
    [
      {
        content: `Address: ${data.address || ""}`,
        colSpan: 3,
        styles: { font: customFontFamily.normalFont, fontStyle: customFontFamily.normal },
      },
    ],
    [
      { content: `Telephone: ${data.telephone || ""}` },
      { content: `Date of Birth: ${data.dob || ""}` },
      { content: `Gender: ${data.gender || ""}` },
    ],
    [
      { content: `Email: ${data.email || ""}` },
      { content: `Employer: ${data.employer || "DYC Global Private Limited"}` },
      { content: `Location: ${data.location || ""}` },
    ],
    [
      {
        content:
          "These tests meet the requirements of EN ISO 9712, NAS 410, EN 4179 and SNT-TC-1A.\nThese tests are suitable for BGAS, PCN and CSWIP examinations.\nAll inspection personnel shall meet the following criteria,",
        colSpan: 3,
        styles: {
          font: customFontFamily.italicFont,
          fontStyle: customFontFamily.italic,
          fontSize: config.fontSizeSm,
          textColor: [50, 50, 50],
          cellPadding: { top: 3, bottom: 3, left: 3.5, right: 3.5 },
        },
      },
    ],
  ];

  autoTable(doc, {
    startY: currentY,
    body: candidateRows,
    theme: "plain",
    styles: { ...getBaseStyles(), fontSize: config.fontSize },
    columnStyles: {
      0: { cellWidth: contentW * 0.44 },
      1: { cellWidth: contentW * 0.33 },
      2: { cellWidth: contentW * 0.23 },
    },
    tableLineColor: [0, 0, 0],
    tableLineWidth: 0.7,
    margin: { left: leftX, right: leftX },
    didDrawCell: (hookData) => {
      doc.setDrawColor(80, 80, 80);
      doc.setLineWidth(0.4);
      doc.rect(hookData.cell.x, hookData.cell.y, hookData.cell.width, hookData.cell.height);
    },
  });

  currentY = doc.lastAutoTable.finalY + 5;

  // ---------------------------------------------------------------------------
  // 3. SECTION 1: NEAR VISION / VISUAL ACUITY
  // ---------------------------------------------------------------------------
  const isUncorrected = data.nearVisionResult === "UNCORRECTED";
  const isCorrectedOne = data.nearVisionResult === "CORRECTED_ONE_EYE";
  const isCorrectedBoth = data.nearVisionResult === "CORRECTED_BOTH_EYES";

  const nearVisionRows = [
    [
      {
        content: "Near Vision/Visual Acuity.",
        styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, fontSize: 8.5 },
      },
    ],
    [
      {
        content:
          "Individual shall be capable of reading any of the following on a standard reading test plate / standard eye test chart, unaided or corrected in at least one eye,\n\n  - N4 Times New Roman (or equivalent), at a distance of not less than 30 cm\n  - Jaeger J1, at a distance of not less than 30 cm\n  - 20/25 Snellen, at a distance of 42 cm ±2.5 cm",
        styles: { fontSize: config.fontSize, cellPadding: { top: 3, bottom: 4, left: 4, right: 4 } },
      },
    ],
    [
      {
        content: "",
        styles: { minCellHeight: 26 },
      },
    ],
  ];

  autoTable(doc, {
    startY: currentY,
    body: nearVisionRows,
    theme: "plain",
    styles: { ...getBaseStyles() },
    tableLineColor: [0, 0, 0],
    tableLineWidth: 0.7,
    margin: { left: leftX, right: leftX },
    didDrawCell: (hookData) => {
      doc.setDrawColor(80, 80, 80);
      doc.setLineWidth(0.4);
      doc.rect(hookData.cell.x, hookData.cell.y, hookData.cell.width, hookData.cell.height);

      // Draw custom checkboxes on row 2 (index 2)
      if (hookData.row.index === 2) {
        const rowY = hookData.cell.y + 4;

        // Checkbox 1: UNCORRECTED
        const x1 = hookData.cell.x + 30;
        doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
        doc.setFontSize(8.5);
        doc.text("UNCORRECTED", x1, rowY + 8);
        drawCheckboxWithTick(doc, x1 + 80, rowY + 1, isUncorrected, 11);

        // Checkbox 2: CORRECTED (Any one Right or Left eye)
        const x2 = hookData.cell.x + 175;
        doc.text("CORRECTED", x2, rowY + 5);
        doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
        doc.setFontSize(7);
        doc.text("(Any one Right or Left eye)", x2, rowY + 13);
        drawCheckboxWithTick(doc, x2 + 105, rowY + 1, isCorrectedOne, 11);

        // Checkbox 3: CORRECTED (Both Eyes)
        const x3 = hookData.cell.x + 355;
        doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
        doc.setFontSize(8.5);
        doc.text("CORRECTED", x3, rowY + 5);
        doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
        doc.setFontSize(7);
        doc.text("(Both Eyes)", x3, rowY + 13);
        drawCheckboxWithTick(doc, x3 + 80, rowY + 1, isCorrectedBoth, 11);
      }
    },
  });

  currentY = doc.lastAutoTable.finalY + 5;

  // ---------------------------------------------------------------------------
  // 4. SECTION 2: COLOUR PERCEPTION / VISION
  // ---------------------------------------------------------------------------
  const isColourAccept = data.colourPerceptionDecision === "ACCEPT";
  const isColourReject = data.colourPerceptionDecision === "REJECT";
  const colourEye = isColourAccept ? data.colourPerceptionEye || "Both Eyes" : "Right, Left, Both Eyes";

  const colourRows = [
    [
      {
        content: "Colour Perception / Vision",
        styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, fontSize: 8.5 },
      },
    ],
    [
      {
        content:
          "To be carried out on candidates who are using Colour perception in in the testing e.g. NDT, welding inspection, Painting etc.\n\nColour perception shall be assessed by the Ishihara 24 plate test. Individual shall be able to distinguish contrast between the colours used in the NDT method concerned as specified by the employer. If misreading any of the first 17 plates is detected during the Ishihara test, a further “trade test” is to be carried out. The candidate’s employer shall be informed where any Colour deficiency is recorded.",
        styles: { fontSize: config.fontSize, cellPadding: { top: 3, bottom: 4, left: 4, right: 4 } },
      },
    ],
    [
      {
        content: "",
        styles: { minCellHeight: 24 },
      },
    ],
  ];

  autoTable(doc, {
    startY: currentY,
    body: colourRows,
    theme: "plain",
    styles: { ...getBaseStyles() },
    tableLineColor: [0, 0, 0],
    tableLineWidth: 0.7,
    margin: { left: leftX, right: leftX },
    didDrawCell: (hookData) => {
      doc.setDrawColor(80, 80, 80);
      doc.setLineWidth(0.4);
      doc.rect(hookData.cell.x, hookData.cell.y, hookData.cell.width, hookData.cell.height);

      if (hookData.row.index === 2) {
        const rowY = hookData.cell.y + 4;

        // ACCEPT
        const x1 = hookData.cell.x + 95;
        doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
        doc.setFontSize(9);
        doc.text("ACCEPT", x1, rowY + 6);
        doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
        doc.setFontSize(7);
        doc.text(`(${colourEye})`, x1 - 15, rowY + 14);
        drawCheckboxWithTick(doc, x1 + 75, rowY + 1, isColourAccept, 11);

        // REJECT
        const x2 = hookData.cell.x + 305;
        doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
        doc.setFontSize(9);
        doc.text("REJECT", x2, rowY + 7);
        drawCheckboxWithTick(doc, x2 + 55, rowY + 1, isColourReject, 11);
      }
    },
  });

  currentY = doc.lastAutoTable.finalY + 5;

  // ---------------------------------------------------------------------------
  // 5. SECTION 3: SHADES OF GREY PERCEPTION / VISION
  // ---------------------------------------------------------------------------
  const isGreyAccept = data.greyPerceptionDecision === "ACCEPT";
  const isGreyReject = data.greyPerceptionDecision === "REJECT";
  const greyEye = isGreyAccept ? data.greyPerceptionEye || "Both Eyes" : "Right, Left, Both Eyes";

  const greyRows = [
    [
      {
        content: "Shades of Grey Perception / Vision",
        styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, fontSize: 8.5 },
      },
    ],
    [
      {
        content:
          "To be carried out on candidates who are conducting tests using shades of grey e.g. Radiographic Testing, TOFD etc.\n\nShades of grey perception shall be assessed by the Skerik grey scale test or equivalent. Grey scale test can be downloaded from CSWIP web site. In specific cases the individual shall be able to differentiate shades of grey used in the NDT method (e.g. radiography). If any difficulty is observed, during this test, a further “trade test” is to be carried out. Any observed difficulty shall be reported to the employer.",
        styles: { fontSize: config.fontSize, cellPadding: { top: 3, bottom: 4, left: 4, right: 4 } },
      },
    ],
    [
      {
        content: "",
        styles: { minCellHeight: 24 },
      },
    ],
  ];

  autoTable(doc, {
    startY: currentY,
    body: greyRows,
    theme: "plain",
    styles: { ...getBaseStyles() },
    tableLineColor: [0, 0, 0],
    tableLineWidth: 0.7,
    margin: { left: leftX, right: leftX },
    didDrawCell: (hookData) => {
      doc.setDrawColor(80, 80, 80);
      doc.setLineWidth(0.4);
      doc.rect(hookData.cell.x, hookData.cell.y, hookData.cell.width, hookData.cell.height);

      if (hookData.row.index === 2) {
        const rowY = hookData.cell.y + 4;

        // ACCEPT
        const x1 = hookData.cell.x + 95;
        doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
        doc.setFontSize(9);
        doc.text("ACCEPT", x1, rowY + 6);
        doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
        doc.setFontSize(7);
        doc.text(`(${greyEye})`, x1 - 15, rowY + 14);
        drawCheckboxWithTick(doc, x1 + 75, rowY + 1, isGreyAccept, 11);

        // REJECT
        const x2 = hookData.cell.x + 305;
        doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
        doc.setFontSize(9);
        doc.text("REJECT", x2, rowY + 7);
        drawCheckboxWithTick(doc, x2 + 55, rowY + 1, isGreyReject, 11);
      }
    },
  });



  currentY = doc.lastAutoTable.finalY + 5;

  // ---------------------------------------------------------------------------
  // 6. SECTION 4: DETAILS OF EXAMINER
  // ---------------------------------------------------------------------------
  const examinerRows = [
    [
      {
        content: "Details of Examiner's (Ophthalmologist / Optometrist / Medical Doctor / ASNT Level III)",
        colSpan: 3,
        styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, fontSize: 8.5 },
      },
    ],
    [
      {
        content: "I confirm that the person mentioned above meets the prescribed standard using the sight correction currently used.",
        colSpan: 3,
        styles: { font: customFontFamily.italicFont, fontStyle: customFontFamily.italic, fontSize: config.fontSizeSm, textColor: [40, 40, 40] },
      },
    ],
    [
      { content: `Organisation: ${data.examinerOrg || "DYC Global Private Limited"}`, colSpan: 2 },
      { content: `Telephone number: ${data.examinerPhone || ""}` },
    ],
    [
      { content: `Name: ${data.examinerName || ""}`, colSpan: 2 },
      { content: `Registration or license number: ${data.examinerRegNo || ""}` },
    ],
    [
      {
        content: `Date of Examination:\n\n${data.examDate || ""}`,
        styles: { minCellHeight: 48, valign: "top" },
      },
      {
        content: `Signature of the Examiner:\n\n${data.examinerSign || ""}`,
        styles: { minCellHeight: 48, valign: "top" },
      },
      {
        content: `Official stamp:\n\n${data.officialStamp || ""}`,
        styles: { minCellHeight: 48, valign: "top", textColor: [180, 20, 20], font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, fontSize: config.fontSizeSm },
      },
    ],
  ];

  autoTable(doc, {
    startY: currentY,
    body: examinerRows,
    theme: "plain",
    styles: { ...getBaseStyles() },
    columnStyles: {
      0: { cellWidth: contentW * 0.35 },
      1: { cellWidth: contentW * 0.35 },
      2: { cellWidth: contentW * 0.30 },
    },
    tableLineColor: [0, 0, 0],
    tableLineWidth: 0.7,
    margin: { left: leftX, right: leftX },
    didDrawCell: (hookData) => {
      doc.setDrawColor(80, 80, 80);
      doc.setLineWidth(0.4);
      doc.rect(hookData.cell.x, hookData.cell.y, hookData.cell.width, hookData.cell.height);
    },
  });

  // ---------------------------------------------------------------------------
  // 7. DECORATORS (Outer Border, Header, Footers)
  // ---------------------------------------------------------------------------
  addPageDecorators(doc, data);

  return doc;
};

export default generateAcuityRecordPDF;
